import { Dayjs, duration } from 'dayjs'
import { Duration } from 'dayjs/plugin/duration'
import stringify from 'fast-safe-stringify'

import { DATE_TEMPLATES, emptyDuration, format, OpeningHours as GanttOpeningHours, string_ToTimeString } from '~/astra'
import { Location, LocationServiceOverride } from '~/storm'
import { ClosedReason } from '~/utils/locationServiceOverride'
import { findServicePeriod, ServicePeriod } from '~/utils/servicePeriods'

/**
 * Whether a location is open on a given day, mirroring the opening hours module's `OpeningHoursStatus`.
 * Only open days have hours. A day without known hours is `not_configured`, not closed, so it still
 * counts as a regular location-day; e.g. a weekend surgery isn't flagged as booked on a closed room.
 * `isClosed` is kept alongside `status` for consumers that only care about closures.
 */
export type LocationDayOpeningStatus = LocationDayOpen | LocationDayClosed | LocationDayNotConfigured

export type LocationDayOpen = { status: 'open'; isClosed: false } & OpeningWindow & OpeningDurations

export type LocationDayClosed = { status: 'closed'; isClosed: true; closedReason: ClosedReason }

export type LocationDayNotConfigured = { status: 'not_configured'; isClosed: false }

type OpeningWindow = { open: string; close: string }

type OpeningDurations = {
    duration: Duration
    changeDuration: Duration
    lunchDuration: Duration
}

function getClosedStatus({
    locationServiceOverride,
    servicePeriod,
}: {
    locationServiceOverride: LocationServiceOverride | undefined
    servicePeriod: ServicePeriod | undefined
}): LocationDayClosed | undefined {
    // Location service overrides take precedence over service periods
    if (locationServiceOverride) {
        return { status: 'closed', isClosed: true, closedReason: locationServiceOverride.closed_reason }
    }

    if (servicePeriod?.is_closed) {
        return { status: 'closed', isClosed: true, closedReason: 'service_period' }
    }

    return undefined
}

// Temporary hardcoded implementation suffices.
// We can create an entity for it later when it's more apparent what kind of solution we need
const isoMonday = 1
const isoTuesday = isoMonday + 1
const isoWednesday = isoTuesday + 1
const isoThursday = isoWednesday + 1
const isoFriday = isoThursday + 1

function getMockOpeningWindow(date: Dayjs, _location: Location): OpeningWindow | undefined {
    if (
        date.isoWeekday() === isoMonday ||
        date.isoWeekday() === isoTuesday ||
        date.isoWeekday() === isoWednesday ||
        date.isoWeekday() === isoThursday ||
        date.isoWeekday() === isoFriday
    ) {
        return { open: '8:00', close: '15:30' }
    }
}

function getOpeningWindowDuration(openingWindow: OpeningWindow) {
    const [openingHour, openingMinute] = openingWindow.open.split(':')
    const [closingHour, closingMinute] = openingWindow.close.split(':')

    const duration_h = Number(closingHour) - Number(openingHour) + (Number(closingMinute) - Number(openingMinute)) / 60
    return duration({ hours: duration_h })
}

function toDurationOrEmptyDuration(isoDuration: string | null | undefined): Duration {
    return isoDuration ? duration(isoDuration) : emptyDuration
}

function getOpeningDurations(openingWindow: OpeningWindow, servicePeriod: ServicePeriod | undefined): OpeningDurations {
    return {
        duration: getOpeningWindowDuration(openingWindow),
        changeDuration: toDurationOrEmptyDuration(servicePeriod?.change_duration),
        lunchDuration: toDurationOrEmptyDuration(servicePeriod?.lunch_duration),
    }
}

export function getOpeningHours({
    date,
    location,
    locationServiceOverride,
}: {
    date: Dayjs
    location: Location
    locationServiceOverride: LocationServiceOverride | undefined
}): LocationDayOpeningStatus {
    const servicePeriod = findServicePeriod({ date, location })

    const closedStatus = getClosedStatus({ locationServiceOverride, servicePeriod })

    if (closedStatus) {
        return closedStatus
    }

    const openingWindow = servicePeriod ? { open: servicePeriod.open, close: servicePeriod.close } : getMockOpeningWindow(date, location)

    if (openingWindow === undefined) {
        console.error(
            'Opening hours not known for location:',
            stringify({
                room_code: location.room_code,
                date: format(date, DATE_TEMPLATES.iso),
            })
        )

        return { status: 'not_configured', isClosed: false }
    }
    return { ...openingWindow, ...getOpeningDurations(openingWindow, servicePeriod), status: 'open', isClosed: false }
}

/**
 * Maps an OR-day's opening window from the API (`HH:MM:SS`, or `null` when unknown) to a Gantt row's opening hours.
 * A missing or zero-length window means the day's opening hours are unknown, so the row is left unhatched.
 */
export function toGanttOpeningHours(openTime: string | null | undefined, closeTime: string | null | undefined): GanttOpeningHours | undefined {
    if (!openTime || !closeTime || openTime === closeTime) return undefined

    return { open: string_ToTimeString(openTime), close: string_ToTimeString(closeTime) }
}
